import { Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from '@reduxjs/toolkit';
import { get2DigitCountryCode, getStateCode } from '@/utils/address';
import { getAuthToken, getBidderId, getUserData } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import api from '@/redux/api/shippingQuote';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import ms from 'ms';
import union from 'lodash/union';

/* Max age of estimate in store */
const REDUX_STORE_TIME = ms('30m');

/* Action Types */
export const LOAD_SHIPPING_QUOTE_FAIL = 'la/domain/shippingQuote/LOAD_FAIL';
export const LOAD_SHIPPING_QUOTE_REQUEST = 'la/domain/shippingQuote/LOAD_REQUEST';
export const LOAD_SHIPPING_QUOTE_SUCCESS = 'la/domain/shippingQuote/LOAD_SUCCESS';

/* Reducer */
export const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_SHIPPING_QUOTE_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_SHIPPING_QUOTE_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload.catalogId]),
            };
        case LOAD_SHIPPING_QUOTE_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            time = action.meta.actionTime;

            if (existing[action.meta.catalogId]) {
                existing[action.meta.catalogId] = {
                    currency: action.payload.currency
                        ? action.payload.currency
                        : existing[action.meta.catalogId].currency || '',
                    link: action.payload.link ? action.payload.link : existing[action.meta.catalogId].link || '',
                    quoteId: action.payload.quoteId
                        ? action.payload.quoteId
                        : existing[action.meta.catalogId].quoteId || '',
                    quoteNumber: action.payload.quoteNumber
                        ? action.payload.quoteNumber
                        : existing[action.meta.catalogId].quoteNumber || '',
                    total: action.payload.total ? action.payload.total : existing[action.meta.catalogId].total || '',
                };
            } else {
                existing[action.meta.catalogId] = action.payload;
            }

            loaded[action.meta.catalogId] = time;
            loading = difference(state.loading, [action.meta.catalogId]);
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.shippingQuote;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getShippingQuote: (state: any, selector: any) => any = createSelector(
    [byIdSelector, idSelector],
    (byId, id) => byId[id] || {}
);

const getLoadTimeForShippingQuote = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

export const isShippingQuoteLoading = createSelector([loadingSelector, idSelector], (loading, id) =>
    loading.includes(id)
);

const shouldfetchShippingQuote = (state, catalogId): boolean => {
    if (!catalogId) {
        return false;
    }
    const item = getShippingQuote(state, catalogId);
    if (item) {
        const loaded = getLoadTimeForShippingQuote(state, catalogId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isShippingQuoteLoading(state, catalogId);
    return !loading;
};

export type ShippingQuoteInfoType = {
    catalogId: number;
    invoiceId: number;
    itemIds?: string[];
    shippingAddress?: Address;
};

/* ACTION CREATORS */
const loadItemShippingQuote =
    ({
        catalogId,
        invoiceId,
        itemIds = [],
        shippingAddress = { address1: '', city: '', countryCode: 'US', postalCode: '' },
    }: ShippingQuoteInfoType) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const bidderId = getBidderId(state);

            let { address1, address2, city, countryCode, postalCode, state: stateLocation } = shippingAddress;
            const userData = getUserData(state);

            if (!Boolean(shippingAddress.address1)) {
                address1 = userData.address;
                address2 = userData.address2;
                city = userData.city;
                countryCode = get2DigitCountryCode(userData.country);
                stateLocation = getStateCode(userData.state);
                postalCode = userData.zip;
            }

            dispatch({
                payload: { catalogId },
                type: LOAD_SHIPPING_QUOTE_REQUEST,
            });

            let number = userData.mobileNumber || userData.phoneNumber;
            if (number.indexOf('1-') === 0) {
                number = number.substring(2);
            }

            const response = await api.fetchShippingQuote({
                address: Boolean(address2) ? `${address1}, ${address2}` : address1,
                authToken,
                bidderId,
                catalogId,
                city,
                contact: {
                    name: `${userData.firstName} ${userData.lastName}`,
                    phoneNumber: { number },
                    primaryEmail: userData.email,
                },
                country: countryCode,
                deployment,
                invoiceId,
                itemIds,
                postalCode: postalCode,
                state: stateLocation,
            });

            return dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_SHIPPING_QUOTE_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_SHIPPING_QUOTE_FAIL,
            });
        }
    };

/**
 * Fetches a new shipping quote if one doesn't already exist for the given item or a past estimate is outdated
 */
export const fetchShippingQuoteIfNeeded =
    ({ catalogId, invoiceId, itemIds, shippingAddress }: ShippingQuoteInfoType) =>
    async (dispatch: Function, getState: Function) => {
        if (shouldfetchShippingQuote(getState(), catalogId)) {
            return dispatch(loadItemShippingQuote({ catalogId, invoiceId, itemIds, shippingAddress }));
        }
        return Promise.resolve();
    };

/**
 * Force fetches a shipping quote
 */
export const fetchShippingQuote =
    ({ catalogId, invoiceId, itemIds, shippingAddress }: ShippingQuoteInfoType) =>
    async (dispatch: Function) => {
        return dispatch(loadItemShippingQuote({ catalogId, invoiceId, itemIds, shippingAddress }));
    };

/**
 * Purchases a shipping quote
 */
export const purchaseShippingQuote =
    ({ catalogId, invoiceId }: ShippingQuoteInfoType) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const { quoteId, quoteNumber } = getShippingQuote(state, catalogId);
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                payload: { catalogId },
                type: LOAD_SHIPPING_QUOTE_REQUEST,
            });

            const response = await api.fetchFinalShippingQuote({
                authToken,
                deployment,
                invoiceId,
                quoteId,
                quoteNumber,
            });

            return dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_SHIPPING_QUOTE_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_SHIPPING_QUOTE_FAIL,
            });
        }
    };

export const fetchPurchasedShippingQuote =
    ({ catalogId, invoiceId }: ShippingQuoteInfoType) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                payload: { catalogId },
                type: LOAD_SHIPPING_QUOTE_REQUEST,
            });

            const response = await api.fetchPurchasedShippingQuote({
                authToken,
                deployment,
                invoiceId,
            });

            return dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_SHIPPING_QUOTE_SUCCESS,
            });
        } catch (error) {
            return dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_SHIPPING_QUOTE_FAIL,
            });
        }
    };
